import { useState, useEffect, useCallback } from "react";
import {
  BrandComparisonChartData,
  MetricType,
} from "@/services/api/brandComparison";
import brandComparisonApi from "@/services/api/brandComparison";
import { transformBrandComparisonData } from "@/utils/brandComparisonHelpers";
import type { BrandComparisonData } from "@/services/mock";

interface UseBrandComparisonProps {
  brandId: string;
  brandName: string;
  categoryId: string;
  days?: number;
  metricType?: string;
  modelId?: string;
  initialData?: BrandComparisonData; // 从searchFlow传入的初始数据
}

export const useBrandComparison = ({
  brandId,
  brandName,
  categoryId,
  days = 7,
  // metricType = 'mentionRate,firstMentionRate,firstRecommendationRate',
  // modelId = 'all',
  initialData,
}: UseBrandComparisonProps) => {
  const [loading, setLoading] = useState(!initialData); // 如果有初始数据，则不显示loading
  const [error] = useState<string | null>(null);
  const [currentMetric, setCurrentMetric] = useState<MetricType>("mentionRate");
  const [chartData, setChartData] = useState<BrandComparisonChartData | null>(
    null
  );
  const [rawData, setRawData] = useState<BrandComparisonData | null>(
    initialData || null
  );

  // 加载品牌对比数据
  const loadComparisonData = useCallback(async () => {
    // console.log('useBrandComparison.loadComparisonData - 参数检查:', { brandId, brandName, categoryId, days, hasInitialData: !!initialData })

    // 如果参数完整，重新请求数据
    if (brandId && brandName && categoryId) {
      setLoading(true);
      try {
        const data = await brandComparisonApi.getComparisonData(
          brandId,
          categoryId,
          days
        );
        setRawData(data);
      } catch (error) {
        console.error(
          "useBrandComparison.loadComparisonData - 请求失败:",
          error
        );
      } finally {
        setLoading(false);
      }
    }
    // 如果没有参数但有初始数据，使用初始数据
    else if (initialData) {
      setRawData(initialData);
      setLoading(false);
    }
  }, [brandId, brandName, categoryId, days, initialData]);

  // 切换指标类型 - 不再需要重新请求数据
  const switchMetric = useCallback(
    (newMetric: MetricType) => {
      if (newMetric === currentMetric) return;

      setCurrentMetric(newMetric);

      if (rawData) {
        const transformedData = transformBrandComparisonData(
          rawData,
          newMetric
        );
        setChartData(transformedData);
      }
    },
    [currentMetric, rawData]
  );

  // 转换数据为图表格式
  useEffect(() => {
    if (rawData) {
      const transformedData = transformBrandComparisonData(
        rawData,
        currentMetric
      );
      setChartData(transformedData);
    }
  }, [rawData, currentMetric]);

  // 初始加载和参数变化时重新加载
  useEffect(() => {
    // 只有在参数完整时才加载数据
    if (brandId && brandName && categoryId) {
      loadComparisonData();
    }
  }, [brandId, brandName, categoryId, days, initialData]); // 响应参数变化

  return {
    loading,
    error,
    chartData,
    currentMetric,
    switchMetric,
    reload: loadComparisonData,
  };
};
